import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Checkbox, DatePicker, InputGroup, Select } from '@reapitform/common-ui'
import { useAdminEnums } from '../../api/enums'
import { AdminFormVersionFlags, EnumOption } from '../../api/types'
import {
  FormDetailsValues,
  VERSION_FLAGS,
  formDetailsSchema,
  versionDetailsSchema,
  versionFlagDefaults,
} from './schema'
import { FieldHelp, FieldStack, FlagsGrid, SectionHeading } from './style'

interface FormDetailsFieldsProps {
  formId: string
  onSubmit: SubmitHandler<FormDetailsValues>
  /** Prefill values (edit mode); omit for a blank create form. */
  initialValues?: Partial<FormDetailsValues>
  /** Hides the "Form details" section and drops its validation — for adding a new version to a form that
   * already exists, where the form's own name/type/region/country are fixed and irrelevant to the version
   * being created. */
  hideFormFields?: boolean
}

/** Picks only the defined flag values out of `initialValues` so create defaults aren't clobbered. */
const flagOverrides = (initialValues?: Partial<FormDetailsValues>): Partial<AdminFormVersionFlags> => {
  const overrides: Partial<AdminFormVersionFlags> = {}
  if (!initialValues) return overrides
  VERSION_FLAGS.forEach(({ name }) => {
    const value = initialValues[name]
    if (typeof value === 'boolean') overrides[name] = value
  })
  return overrides
}

/**
 * Shared form-details form (form fields + first/current version fields + flags). Text inputs bind via
 * `register`; Selects, DatePicker and Checkboxes are controlled locally and pushed into RHF with
 * `setValue`. Prefills from `initialValues` when editing. The submit button lives in the drawer footer
 * and triggers this form via the shared `formId`.
 */
export const FormDetailsFields: React.FC<FormDetailsFieldsProps> = ({
  formId,
  onSubmit,
  initialValues,
  hideFormFields,
}) => {
  const { data: enums } = useAdminEnums()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormDetailsValues>({
    resolver: yupResolver(hideFormFields ? versionDetailsSchema : formDetailsSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      fullName: initialValues?.fullName ?? '',
      formType: initialValues?.formType ?? '',
      region: initialValues?.region ?? '',
      country: initialValues?.country ?? '',
      version: initialValues?.version ?? '',
      validFromUTC: initialValues?.validFromUTC ?? '',
      ...versionFlagDefaults,
      ...flagOverrides(initialValues),
    },
  })

  const [formTypeOption, setFormTypeOption] = useState<EnumOption | null>(null)
  const [regionOption, setRegionOption] = useState<EnumOption | null>(null)
  const [countryOption, setCountryOption] = useState<EnumOption | null>(null)
  const [validFrom, setValidFrom] = useState(initialValues?.validFromUTC ?? '')

  // Prefill the selects from initialValues once the enum options are available.
  useEffect(() => {
    if (!enums) return
    if (initialValues?.formType)
      setFormTypeOption(enums.FormType?.find((o) => o.value === initialValues.formType) ?? null)
    if (initialValues?.region) setRegionOption(enums.Region?.find((o) => o.value === initialValues.region) ?? null)
    if (initialValues?.country) setCountryOption(enums.Country?.find((o) => o.value === initialValues.country) ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enums])

  const isNewZealand = countryOption?.value === 'NZ'

  const handleSelect = (
    option: EnumOption | null,
    field: 'formType' | 'region' | 'country',
    setOption: (value: EnumOption | null) => void,
  ) => {
    setOption(option)
    setValue(field, option?.value ?? '', { shouldValidate: true })
  }

  const handleCountryChange = (option: EnumOption | null) => {
    handleSelect(option, 'country', setCountryOption)
    if (option?.value === 'NZ') {
      setRegionOption(null)
      setValue('region', '', { shouldValidate: true })
    }
  }

  const handleValidFrom = (value: string | string[]) => {
    const next = Array.isArray(value) ? (value[0] ?? '') : value
    setValidFrom(next)
    setValue('validFromUTC', next, { shouldValidate: true })
  }

  return (
    <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
      {!hideFormFields && (
        <>
          <SectionHeading>Form details</SectionHeading>
          <InputGroup
            label="Name *"
            placeholder="Short form name"
            {...register('name')}
            errorMessage={errors.name?.message}
          />
          <InputGroup
            label="Full Name"
            placeholder="Full descriptive name"
            {...register('fullName')}
            errorMessage={errors.fullName?.message}
          />
          <div>
            <Select
              label="Form Type *"
              options={enums?.FormType ?? []}
              value={formTypeOption}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => handleSelect(option, 'formType', setFormTypeOption)}
              errorMessage={errors.formType?.message}
            />
            {formTypeOption?.family && (
              <FieldHelp>
                Family: {formTypeOption.family}. Set by the form type, not editable directly — it decides whether this
                form gets a tenant-signing flow (Agreement) or is emailed with no signing (Notice).
              </FieldHelp>
            )}
          </div>
          <Select
            label="Country *"
            options={enums?.Country ?? []}
            value={countryOption}
            getOptionLabel={(option: EnumOption) => option.label}
            getOptionValue={(option: EnumOption) => option.value}
            onChange={handleCountryChange}
            errorMessage={errors.country?.message}
          />
          <Select
            label="Region"
            isClearable
            disabled={isNewZealand}
            options={enums?.Region ?? []}
            value={regionOption}
            getOptionLabel={(option: EnumOption) => option.label}
            getOptionValue={(option: EnumOption) => option.value}
            onChange={(option: EnumOption | null) => handleSelect(option, 'region', setRegionOption)}
            errorMessage={errors.region?.message}
          />
        </>
      )}

      <SectionHeading>Form Version Details</SectionHeading>
      <InputGroup
        label="Version *"
        placeholder='e.g. "Form 18a"'
        {...register('version')}
        errorMessage={errors.version?.message}
      />
      <DatePicker
        label="Valid From *"
        value={validFrom}
        onChange={handleValidFrom as any}
        errorMessage={errors.validFromUTC?.message}
      />

      <SectionHeading>Form Version Settings</SectionHeading>
      <FlagsGrid>
        {VERSION_FLAGS.filter((flag) => flag.group === 'applies').map((flag) => (
          <Checkbox
            key={flag.name}
            label={flag.label}
            checked={Boolean(watch(flag.name))}
            onChange={() => setValue(flag.name, !watch(flag.name))}
          />
        ))}
      </FlagsGrid>

      {/* Shown regardless of family, not hidden for a Notice — these flags are still read unconditionally
        by the recipient/representee component factories whenever that section exists, so a stored `true`
        would still inject its fields even if the checkbox were hidden. "Agreement-only" describes the
        *concept* (tenant-signing consent, or consent for how future notices under an agreement are
        served) not to be confused with the Notices document family itself. */}
      <SectionHeading>Agreement-only — no practical effect on a Notice</SectionHeading>
      <FlagsGrid>
        {VERSION_FLAGS.filter((flag) => flag.group === 'agreement-only').map((flag) => (
          <Checkbox
            key={flag.name}
            label={flag.label}
            checked={Boolean(watch(flag.name))}
            onChange={() => setValue(flag.name, !watch(flag.name))}
          />
        ))}
      </FlagsGrid>
    </FieldStack>
  )
}
